import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowRight, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDateSr } from "@normax-performance/shared-format";
import { analyticsApi, normRecommendationsApi } from "../../lib/api-client/endpoints";
import { Button, Donut, MetricChip, PerformanceHero } from "../../shared-ui/primitives";
import { useLocale } from "../../i18n/LocaleProvider";
import { formatAnalyticsHoursMinutes, formatAnalyticsMinutes, formatAnalyticsPercent, formatAnalyticsTrend } from "../../lib/format";
import { ZoneDistribution } from "../analytics/components/ZoneDistribution";
import "../analytics/AnalyticsPage.css";
import { AttentionList } from "./components/AttentionList";
import { CompletenessSignal } from "./components/CompletenessSignal";
import { selectAttentionWorkers } from "./attention";
import "./DashboardPage.css";

/**
 * NormaX Performance Intelligence — Dashboard/Pregled (docs/
 * NORMAX_PERFORMANCE_REPORTING_DAY_DASHBOARD_IMPLEMENTATION_REPORT.md).
 * Shows the tenant's LATEST RECORDED DAY — never blindly "yesterday" — via
 * the shared server-side Reporting Day resolver (`GET
 * /analytics/latest-reporting-day`, `ReportingDayService`). No period
 * picker (that's Analitika's job). Consumes the same
 * `/analytics/summary`/`/analytics/workers` endpoints Analytics itself
 * uses; never recomputes Method C, zones, ranking, or trend in the
 * frontend.
 *
 * Request flow: resolve the reporting day ONCE, then request
 * summary/workers for exactly that date — never a client-side backward-day
 * polling loop (Saturday→Friday→Thursday…) from the browser. If the
 * resolver returns `null` (a brand-new tenant with zero historical work),
 * summary/workers are never called at all.
 *
 * The header's date label distinguishes "the resolved day happens to be
 * yesterday" ("Juče · dd.mm.gggg") from "the resolved day is older"
 * ("Poslednji evidentirani dan · dd.mm.gggg") using the response's own
 * `relation` field — computed server-side against `Company.timezone` — and
 * ONLY that. This component deliberately does NOT compute its own "is this
 * yesterday?" comparison from the browser's local clock: the server is the
 * sole authority for the company-local calendar context (a Director's
 * browser can be in a different timezone than the tenant, or the check can
 * straddle a calendar-day boundary — either way, only the server's own
 * notion of "yesterday" may decide the label). The displayed calendar date
 * itself remains the server-resolved reporting date either way.
 */
export function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const { t: tCommon } = useTranslation("common");
  const { intlLocale } = useLocale();
  const navigate = useNavigate();

  const reportingDayQuery = useQuery({
    queryKey: ["analytics", "latest-reporting-day"],
    queryFn: () => analyticsApi.latestReportingDay(),
  });

  // `undefined` while still resolving, `null` = no historical work at all,
  // a string = the resolved date to display/query.
  const resolvedDate = reportingDayQuery.data?.date;
  const hasResolvedDate = typeof resolvedDate === "string";
  // Server-computed relation to company-local "yesterday" — never
  // recomputed here. "EARLIER" is the only fallback-labeling signal.
  const relation = reportingDayQuery.data?.relation ?? null;

  // Same query keys Analytics itself uses for summary/workers — an
  // Analytics "Juče" view and this Dashboard share react-query's cache
  // whenever they happen to request the identical (date, date) range.
  const summaryQuery = useQuery({
    queryKey: ["analytics", "summary", resolvedDate, resolvedDate],
    queryFn: () => analyticsApi.summary(resolvedDate as string, resolvedDate as string),
    enabled: hasResolvedDate,
  });
  const workersQuery = useQuery({
    queryKey: ["analytics", "workers", resolvedDate, resolvedDate],
    queryFn: () => analyticsApi.workers(resolvedDate as string, resolvedDate as string),
    enabled: hasResolvedDate,
  });
  const recommendationsQuery = useQuery({
    queryKey: ["norm-recommendations"],
    queryFn: () => normRecommendationsApi.list(),
  });
  // The endpoint now returns one entry per norm with enough sample (not
  // just outliers, since the Norms-tab "Prosek" column needs every norm's
  // average) — the card must count only the ones actually outside bounds.
  const recommendationCount = recommendationsQuery.data?.filter((r) => r.isOutsideBounds).length ?? 0;

  // Defensive-only fallback: the resolver guarantees the resolved date has
  // >=1 WorkLine, so entryCount === 0 should not happen in practice for
  // that same date. Kept as a calm, non-alarming safety net (same copy as
  // before) rather than assumed impossible.
  const isEmptyDay = summaryQuery.data ? summaryQuery.data.completeness.entryCount === 0 : false;
  const goToAnalytics = () => navigate("/analitika");

  const attention = useMemo(() => {
    if (!workersQuery.data) return { shown: [], totalBelowCount: 0 };
    return selectAttentionWorkers(workersQuery.data.eligible);
  }, [workersQuery.data]);

  const overall = summaryQuery.data?.overall;
  const dateDisplay = hasResolvedDate ? formatDateSr(resolvedDate) : "";
  const isFallbackDay = relation === "EARLIER";

  if (reportingDayQuery.isError) {
    return (
      <div className="nx-dashboard">
        <div className="nx-dashboard__header">
          <h1 className="nx-dashboard__title">{t("title")}</h1>
        </div>
        <div className="nx-dashboard__error">
          <p className="nx-dashboard__error-title">{t("error.title")}</p>
          <p>{t("error.description")}</p>
          <Button variant="secondary" onClick={() => reportingDayQuery.refetch()}>
            <RefreshCw size={16} />
            {t("error.retry")}
          </Button>
        </div>
      </div>
    );
  }

  if (reportingDayQuery.isSuccess && !hasResolvedDate) {
    // No historical WorkLine at all for this tenant — honest empty state,
    // never a fake "yesterday" date, never a wall of zeros.
    return (
      <div className="nx-dashboard">
        <div className="nx-dashboard__header">
          <h1 className="nx-dashboard__title">{t("title")}</h1>
          <p className="nx-dashboard__subtitle">{t("subtitle")}</p>
        </div>
        <div className="nx-dashboard__empty">
          <p className="nx-dashboard__empty-title">{t("noHistory.title")}</p>
          <p>{t("noHistory.hint")}</p>
          <Button variant="secondary" onClick={goToAnalytics}>
            <ArrowRight size={16} />
            {t("cta")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="nx-dashboard">
      <div className="nx-dashboard__header">
        <h1 className="nx-dashboard__title">{t("title")}</h1>
        {hasResolvedDate && (
          <p className="nx-dashboard__date-context">
            {isFallbackDay ? t("dateContext.fallback", { date: dateDisplay }) : t("dateContext.yesterday", { date: dateDisplay })}
          </p>
        )}
        <p className="nx-dashboard__subtitle">{t("subtitle")}</p>
        {hasResolvedDate && isFallbackDay && !isEmptyDay && (
          <p className="nx-dashboard__freshness-note">{t("freshnessNote")}</p>
        )}
      </div>

      {summaryQuery.isError ? (
        <div className="nx-dashboard__error">
          <p className="nx-dashboard__error-title">{t("error.title")}</p>
          <p>{t("error.description")}</p>
          <Button variant="secondary" onClick={() => summaryQuery.refetch()}>
            <RefreshCw size={16} />
            {t("error.retry")}
          </Button>
        </div>
      ) : isEmptyDay ? (
        <div className="nx-dashboard__empty">
          <p className="nx-dashboard__empty-title">{t("empty.title", { date: dateDisplay })}</p>
          <p>{t("empty.hint")}</p>
          <Button variant="secondary" onClick={goToAnalytics}>
            <ArrowRight size={16} />
            {t("cta")}
          </Button>
        </div>
      ) : (
        <>
          <div className="nx-dashboard__hero-row">
            <PerformanceHero
              label={t("hero.title")}
              isLoading={!hasResolvedDate || summaryQuery.isLoading}
              percentDisplay={overall?.performancePercentLabel ? formatAnalyticsPercent(overall.performancePercentLabel, intlLocale) : null}
              unavailableLabel={t("notAvailable")}
              zone={overall?.zone?.zone ?? null}
              zoneBadgeLabel={overall?.zone ? tCommon(`performanceZone.${overall.zone.zone}`) : undefined}
              trendDisplay={formatAnalyticsTrend(overall?.trend.trendPercentagePoints ?? null)}
              trendDirection={overall?.trend.direction ?? null}
              trendSuffix={t("hero.trendVsPrevious")}
              caption={t("hero.performanceHelp")}
            />
            <div className="nx-dashboard__hero-metrics">
              <MetricChip
                label={t("metrics.totalRecordedMinutes")}
                display={
                  !hasResolvedDate || summaryQuery.isLoading || !summaryQuery.data
                    ? null
                    : formatAnalyticsHoursMinutes(summaryQuery.data.time.distribution.totalRecordedMinutes, intlLocale)
                }
              />
              <MetricChip
                label={t("metrics.normedMinutes")}
                display={!hasResolvedDate || summaryQuery.isLoading || !overall ? null : formatAnalyticsMinutes(overall.normedMinutes, intlLocale)}
              />
            </div>
          </div>

          <div className="nx-dashboard__row">
            <CompletenessSignal
              workers={summaryQuery.data?.workers ?? { activeWorkerCount: 0, recordedWorkerCount: 0, eligibleWorkerCount: 0, below90Count: 0, between90And100Count: 0, above100Count: 0 }}
              completeness={summaryQuery.data?.completeness ?? { entryCount: 0, draftEntryCount: 0, finishedEntryCount: 0, activeWorkersWithoutEntryCount: 0 }}
              isLoading={!hasResolvedDate || summaryQuery.isLoading}
            />
            <ZoneDistribution summary={summaryQuery.data} isLoading={!hasResolvedDate || summaryQuery.isLoading} />
          </div>

          {recommendationCount > 0 ? (
            <div className="nx-dashboard__recommendation-card">
              <p>{t("normRecommendations.count", { count: recommendationCount })}</p>
              <Button variant="primary" size="sm" onClick={() => navigate("/sifarnici?tab=norms&recommended=1")}>
                <ArrowRight size={16} />
                {t("normRecommendations.cta")}
              </Button>
            </div>
          ) : null}

          {workersQuery.isError ? (
            <div className="nx-dashboard__attention">
              <p className="nx-dashboard__section-title">{t("attention.title")}</p>
              <p>{t("attentionError.description")}</p>
              <Button variant="secondary" size="sm" onClick={() => workersQuery.refetch()}>
                <RefreshCw size={16} />
                {t("attentionError.retry")}
              </Button>
            </div>
          ) : (
            <AttentionList
              shown={attention.shown}
              totalBelowCount={attention.totalBelowCount}
              isLoading={!hasResolvedDate || workersQuery.isLoading}
              onViewAll={goToAnalytics}
            />
          )}

          <div className="nx-dashboard__operational-row">
            <div className="nx-dashboard__time-dist">
              <div className="nx-dashboard__time-dist-content">
                <p className="nx-dashboard__section-title">{t("operational.timeDistributionTitle")}</p>
                {!hasResolvedDate || summaryQuery.isLoading || !summaryQuery.data ? (
                  <span className="nx-skeleton" style={{ width: "100%", height: "48px" }} />
                ) : (
                  <Donut
                    segments={[
                      { key: "normed", value: summaryQuery.data.time.distribution.normedShare ?? 0, label: t("operational.normedWork"), minutes: summaryQuery.data.time.distribution.normedMinutes, colorVar: "--color-row-normed" },
                      { key: "neutral", value: summaryQuery.data.time.distribution.neutralShare ?? 0, label: t("operational.neutralMinutes"), minutes: summaryQuery.data.time.distribution.neutralMinutes, colorVar: "--color-row-neutral" },
                      { key: "unnormed", value: summaryQuery.data.time.distribution.unnormedShare ?? 0, label: t("operational.unnormedMinutes"), minutes: summaryQuery.data.time.distribution.unnormedMinutes, colorVar: "--color-row-unnormed" },
                    ]}
                    ariaLabel={t("operational.timeDistributionTitle")}
                    intlLocale={intlLocale}
                    hasData={summaryQuery.data.time.distribution.normedShare !== null}
                    minutesDisplay="hours"
                    centerPrimary={formatAnalyticsHoursMinutes(summaryQuery.data.time.distribution.totalRecordedMinutes, intlLocale)}
                  />
                )}
                <p className="nx-dashboard__time-dist-note">{t("operational.explanation")}</p>
              </div>
              <Button variant="primary" onClick={goToAnalytics}>
                <ArrowRight size={16} />
                {t("cta")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
